// src/store/slices/gameSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import Gateway from '../../api/gateway';

// ============================================================================
// ВСПОМОГАТЕЛЬНЫЕ ФУНКЦИИ ДЛЯ ID
// ============================================================================

const extractId = (value) => {
  if (!value) return null;
  
  if (typeof value === 'string') {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    const objectIdRegex = /^[0-9a-fA-F]{24}$/;
    const numericRegex = /^\d+$/;
    
    if (uuidRegex.test(value) || objectIdRegex.test(value) || numericRegex.test(value)) {
      return value;
    }
    return null;
  }
  
  if (typeof value === 'number') {
    return value.toString();
  }
  
  if (value && typeof value === 'object') {
    if (value.id !== undefined) return extractId(value.id);
    if (value.character_id !== undefined) return extractId(value.character_id);
    if (value._id !== undefined) return extractId(value._id);
    if (value.image_bit_id !== undefined) return extractId(value.image_bit_id);
  }
  
  return null;
};

const validateId = (id) => {
  const extracted = extractId(id);
  if (!extracted) return false;
  
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  const objectIdRegex = /^[0-9a-fA-F]{24}$/;
  const numericRegex = /^\d+$/;
  
  return uuidRegex.test(extracted) || objectIdRegex.test(extracted) || numericRegex.test(extracted);
};

// ============================================================================
// АСИНХРОННЫЕ THUNKS
// ============================================================================

export const loadCharacters = createAsyncThunk(
  'game/loadCharacters',
  async (params = {}, { rejectWithValue, getState }) => {
    try {
      let characters = [];
      
      const userId = String(params.userId || '').trim();
      
      if (!userId && params.mode !== 'admin') {
        throw new Error('User ID is required for loading characters');
      }
      
      if (params.mode === 'admin') {
        const response = await Gateway.http.universe.get('/api/admin/characters', {
          params: { include_npc: true }
        });
        characters = response.data?.characters || response.data?.data?.characters || [];
      } else {
        console.log(`📥 Загрузка персонажей для пользователя: "${userId}"`);
        const response = await Gateway.api.character.getCharactersByPlayer(userId);
        characters = Gateway.utils.characters.extractDataFromResponse(response, 'characters');
      }
      
      const validCharacters = characters.filter(char => {
        const charId = extractId(char);
        return validateId(charId);
      }).map(char => {
        const isActive = char.status === 'active';
        return {
          ...char,
          id: extractId(char),
          is_active: isActive
        };
      });
      
      console.log(`✅ Загружено ${validCharacters.length} персонажей`);
      
      return { characters: validCharacters, mode: params.mode };
    } catch (error) {
      console.error('❌ Ошибка загрузки персонажей:', error);
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const selectCharacter = createAsyncThunk(
  'game/selectCharacter',
  async (characterId, { rejectWithValue, getState }) => {
    try {
      console.log(`🎯 Выбор персонажа: ${characterId} (type: ${typeof characterId})`);
      
      if (!validateId(characterId)) {
        throw new Error(`Invalid character ID: ${characterId}`);
      }
      
      let character;
      
      try {
        const response = await Gateway.api.character.getCharacter(characterId);
        character = response.data || response;
        console.log('✅ Персонаж загружен из API');
      } catch (apiError) {
        console.warn('⚠️ Не удалось загрузить персонажа из API, используем локальные данные');
        const state = getState();
        character = state.game.characters.find(c => c.id === characterId);
        if (!character) {
          throw new Error(`Персонаж с ID ${characterId} не найден ни в API, ни в локальном состоянии`);
        }
        console.log('✅ Персонаж найден в локальном состоянии');
      }
      
      const isActive = character.status === 'active';
      character.id = characterId;
      character.is_active = isActive;
      
      localStorage.setItem('selected_character_id', characterId.toString());
      localStorage.setItem('character_data', JSON.stringify(character));
      
      console.log('✅ Персонаж выбран:', character.name);
      return character;
      
    } catch (error) {
      console.error('❌ Ошибка выбора персонажа:', error);
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const createCharacter = createAsyncThunk(
  'game/createCharacter',
  async (characterData, { rejectWithValue }) => {
    try {
      console.log('🎨 Создание нового персонажа...', characterData);
      const safeData = {
        ...characterData,
        user_id: String(characterData.user_id || '').trim()
      };
      const rawResponse = await Gateway.api.character.createCharacter(safeData);
      console.log('📥 Ответ от сервера при создании персонажа:', rawResponse);

      const responseData = rawResponse.data || rawResponse;
      const character = responseData.character || responseData;

      if (!character || !character.image_bit_id) {
        throw new Error(`Server did not return valid character data. Full response: ${JSON.stringify(rawResponse)}`);
      }

      const characterId = character.image_bit_id;

      if (!validateId(characterId)) {
        throw new Error(`Server returned invalid character ID: "${characterId}". Full response: ${JSON.stringify(rawResponse)}`);
      }

      const isActive = character.status === 'active';
      const finalCharacter = {
        id: characterId,
        ...character,
        user_id: safeData.user_id,
        created_at: character.created_at || new Date().toISOString(),
        image_bit_id: characterId,
        is_active: isActive,
        spawn_object_id: character.spawn_object_id || character.current_spawn_object_id || null,
        current_world_id: character.current_world_id || character.world_id || null,
        in_world: character.in_world === true || character.placed_in_world === true || false
      };

      console.log(`✅ Персонаж создан: ${finalCharacter.name} (bit_id: ${finalCharacter.id})`);
      return finalCharacter;
    } catch (error) {
      console.error('❌ Ошибка создания персонажа:', error);
      return rejectWithValue(error.message || 'Ошибка создания персонажа');
    }
  }
);

export const updateCharacter = createAsyncThunk(
  'game/updateCharacter',
  async ({ characterId, updates }, { rejectWithValue }) => {
    try {
      const id = extractId(characterId);
      
      if (!validateId(id)) {
        throw new Error(`Invalid character ID: ${characterId}`);
      }
      
      console.log(`✏️ Обновление персонажа ${id}`);
      const response = await Gateway.api.character.updateCharacter(id, updates);
      const character = response.data || response;
      
      const isActive = character.status === 'active';
      character.id = extractId(character);
      character.is_active = isActive;
      
      return character;
    } catch (error) {
      console.error('❌ Ошибка обновления персонажа:', error);
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const deleteCharacter = createAsyncThunk(
  'game/deleteCharacter',
  async (characterId, { rejectWithValue }) => {
    try {
      const id = extractId(characterId);
      
      if (!validateId(id)) {
        throw new Error(`Invalid character ID: ${characterId}`);
      }
      
      console.log(`🗑️ Удаление персонажа ${id}`);
      await Gateway.api.character.deleteCharacter(id);
      
      const selectedId = localStorage.getItem('selected_character_id');
      if (selectedId && selectedId === id) {
        localStorage.removeItem('selected_character_id');
        localStorage.removeItem('character_data');
      }
      
      return id;
    } catch (error) {
      console.error('❌ Ошибка удаления персонажа:', error);
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const activateCharacter = createAsyncThunk(
  'game/activateCharacter',
  async (characterId, { rejectWithValue }) => {
    try {
      const id = extractId(characterId);
      
      if (!validateId(id)) {
        throw new Error(`Invalid character ID: ${characterId}`);
      }
      
      console.log(`▶️ Активация персонажа ${id}`);
      const response = await Gateway.api.activeCharacter.activateCharacter(id);
      const character = response.data || response;
      
      character.id = extractId(character);
      character.is_active = true;
      character.status = 'active';
      
      return character;
    } catch (error) {
      console.error('❌ Ошибка активации персонажа:', error);
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const deactivateCharacter = createAsyncThunk(
  'game/deactivateCharacter',
  async (characterId, { rejectWithValue }) => {
    try {
      const id = extractId(characterId);
      
      if (!validateId(id)) {
        throw new Error(`Invalid character ID: ${characterId}`);
      }
      
      console.log(`⏸️ Деактивация персонажа ${id}`);
      const response = await Gateway.api.activeCharacter.deactivateCharacter(id);
      const character = response.data || response;
      
      character.id = extractId(character);
      character.is_active = false;
      character.status = 'design';
      
      return character;
    } catch (error) {
      console.error('❌ Ошибка деактивации персонажа:', error);
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// ============================================================================
// INITIAL STATE
// ============================================================================

// 🔥 Итерация 7: дефолтное состояние инвентаря
const EMPTY_INVENTORY = {
  items: [],
  capacity: 100,
  used: 0,
  credits: 0,
  weight_used: 0.0,
  volume_used: 0.0,
};

const initialState = {
  characters: [],
  filteredCharacters: [],
  selectedCharacter: null,
  selectedCharacterData: null,
  loading: false,
  loadingCharacters: false,
  error: null,
  success: null,
  filters: {},
  mode: 'player',
  stats: {
    total: 0,
    player: 0,
    npc: 0,
    active: 0,
    placed: 0
  },
  // 🔧 ИГРОВЫЕ ДАННЫЕ
  gameData: {
    emotions: {},
    goals: [],
    tasks: [],
    intents: [],
    locationInfo: null,
    environment: {},
    availableActions: [],
    consciousness: {
      model_summary: {},
      context_focus: {
        focus: 'idle',
        emotional_driver: 'neutral',
        environment_threat: 'low',
        attention_span: 'medium',
        cognitive_load: 30
      },
      recent_reflections: [],
      crkv_metrics: {
        crk: 50,
        k: 50,
        v: 50,
        overall_confidence: 50
      }
    },
    detailedTasks: []
  },
  chatMessages: [],
  isDetailedTasksLoading: false,

  // 🔥 Итерация 7: инвентарь
  inventory: { ...EMPTY_INVENTORY },
  inventoryLoading: true,
  inventoryError: null,
};

// ============================================================================
// НОРМАЛИЗАЦИЯ ИНВЕНТАРЯ
// ============================================================================

function normalizeInventoryPayload(raw) {
  if (!raw || typeof raw !== 'object') return { ...EMPTY_INVENTORY };

  const items = Array.isArray(raw.items) ? raw.items : [];

  return {
    items,
    capacity: typeof raw.capacity === 'number' ? raw.capacity : 100,
    used: typeof raw.used === 'number' ? raw.used : items.length,
    credits: typeof raw.credits === 'number' ? raw.credits : 0,
    weight_used: typeof raw.weight_used === 'number' ? raw.weight_used : 0.0,
    volume_used: typeof raw.volume_used === 'number' ? raw.volume_used : 0.0,
  };
}

// ============================================================================
// SLICE
// ============================================================================

const gameSlice = createSlice({
  name: 'game',
  initialState,
  reducers: {
    setMode: (state, action) => {
      state.mode = action.payload;
      state.selectedCharacter = null;
      state.selectedCharacterData = null;
      state.filters = {};
    },
    
    setFilters: (state, action) => {
      state.filters = action.payload;
      
      let filtered = state.characters;
      
      if (state.filters.name) {
        const searchTerm = state.filters.name.toLowerCase();
        filtered = filtered.filter(c => 
          c.name?.toLowerCase().includes(searchTerm)
        );
      }
      
      if (state.filters.race) {
        filtered = filtered.filter(c => c.race_id === state.filters.race);
      }
      
      if (state.filters.is_npc !== '') {
        const isNPC = state.filters.is_npc === 'true';
        filtered = filtered.filter(c => c.is_npc === isNPC);
      }
      
      if (state.filters.is_active !== '') {
        const isActive = state.filters.is_active === 'true';
        filtered = filtered.filter(c => c.is_active === isActive);
      }
      
      if (state.filters.in_world !== '') {
        const inWorld = state.filters.in_world === 'true';
        filtered = filtered.filter(c => c.in_world === inWorld);
      }
      
      if (state.filters.min_level) {
        filtered = filtered.filter(c => (c.level || 1) >= parseInt(state.filters.min_level));
      }
      
      if (state.filters.max_level) {
        filtered = filtered.filter(c => (c.level || 1) <= parseInt(state.filters.max_level));
      }
      
      state.filteredCharacters = filtered;
      
      state.stats = {
        total: filtered.length,
        player: filtered.filter(c => !c.is_npc).length,
        npc: filtered.filter(c => c.is_npc).length,
        active: filtered.filter(c => c.is_active).length,
        placed: filtered.filter(c => c.in_world).length
      };
    },
    
    clearFilters: (state) => {
      state.filters = {};
      state.filteredCharacters = state.characters;
      
      state.stats = {
        total: state.characters.length,
        player: state.characters.filter(c => !c.is_npc).length,
        npc: state.characters.filter(c => c.is_npc).length,
        active: state.characters.filter(c => c.is_active).length,
        placed: state.characters.filter(c => c.in_world).length
      };
    },
    
    setSelectedCharacter: (state, action) => {
      const characterId = extractId(action.payload);
      
      if (validateId(characterId)) {
        state.selectedCharacter = characterId;
        const character = state.characters.find(c => c.id === characterId);
        state.selectedCharacterData = character || null;
      } else {
        console.warn('Invalid character ID in setSelectedCharacter:', action.payload);
        state.selectedCharacter = null;
        state.selectedCharacterData = null;
      }
    },
    
    clearSelection: (state) => {
      state.selectedCharacter = null;
      state.selectedCharacterData = null;
    },
    
    clearError: (state) => {
      state.error = null;
    },
    
    clearSuccess: (state) => {
      state.success = null;
    },
    
    setError: (state, action) => {
      state.error = action.payload;
      state.loading = false;
      state.loadingCharacters = false;
    },
    
    setSuccess: (state, action) => {
      state.success = action.payload;
    },
    
    updateCharacterState: (state, action) => {
      const characterId = extractId(action.payload);
      
      if (state.selectedCharacter === characterId) {
        state.selectedCharacterData = {
          ...state.selectedCharacterData,
          ...action.payload
        };
      }
      
      const index = state.characters.findIndex(c => c.id === characterId);
      if (index !== -1) {
        state.characters[index] = {
          ...state.characters[index],
          ...action.payload
        };
      }
      
      const filteredIndex = state.filteredCharacters.findIndex(c => c.id === characterId);
      if (filteredIndex !== -1) {
        state.filteredCharacters[filteredIndex] = {
          ...state.filteredCharacters[filteredIndex],
          ...action.payload
        };
      }
    },

    // ============================================================================
    // 🔧 ИГРОВЫЕ ДАННЫЕ
    // ============================================================================

    updateGameData: (state, action) => {
      state.gameData = {
        ...state.gameData,
        ...action.payload
      };
    },

    updateEmotions: (state, action) => {
      if (action.payload && typeof action.payload === 'object' && Object.keys(action.payload).length > 0) {
        state.gameData.emotions = { 
          ...(state.gameData.emotions || {}), 
          ...action.payload 
        };
      }
    },

    updateGoals: (state, action) => {
      if (action.payload !== undefined && action.payload !== null && Array.isArray(action.payload)) {
        state.gameData.goals = action.payload;
      }
    },

    updateTasks: (state, action) => {
      if (action.payload !== undefined && action.payload !== null && Array.isArray(action.payload)) {
        state.gameData.tasks = action.payload;
      }
    },

    updateIntents: (state, action) => {
      if (action.payload !== undefined && action.payload !== null && Array.isArray(action.payload)) {
        state.gameData.intents = action.payload;
      } else {
        state.gameData.intents = [];
      }
    },

    updateLocationInfo: (state, action) => {
      if (action.payload && action.payload.id && action.payload.id !== 'unknown') {
        state.gameData.locationInfo = {
          ...(state.gameData.locationInfo || {}),
          ...action.payload
        };
        
        if (action.payload.environment && typeof action.payload.environment === 'object' && Object.keys(action.payload.environment).length > 0) {
          state.gameData.environment = {
            ...(state.gameData.environment || {}),
            ...action.payload.environment
          };
        }
      }
    },

    updateEnvironment: (state, action) => {
      if (action.payload && typeof action.payload === 'object' && Object.keys(action.payload).length > 0) {
        state.gameData.environment = {
          ...(state.gameData.environment || {}),
          ...action.payload
        };
      }
    },

    updateAvailableActions: (state, action) => {
      if (action.payload !== undefined && Array.isArray(action.payload)) {
        state.gameData.availableActions = action.payload;
      }
    },

    updateConsciousness: (state, action) => {
      if (action.payload && typeof action.payload === 'object' && Object.keys(action.payload).length > 0) {
        state.gameData.consciousness = {
          ...(state.gameData.consciousness || {}),
          ...action.payload
        };
      }
    },

    updateConsciousnessDetails: (state, action) => {
      if (action.payload && typeof action.payload === 'object' && Object.keys(action.payload).length > 0) {
        state.gameData.consciousness = {
          ...(state.gameData.consciousness || {}),
          ...action.payload
        };
      }
    },

    updateDetailedTasks: (state, action) => {
      state.gameData.detailedTasks = Array.isArray(action.payload) ? action.payload : [];
      state.isDetailedTasksLoading = false;
    },

    setDetailedTasksLoading: (state, action) => {
      state.isDetailedTasksLoading = !!action.payload;
    },

    resetGameData: (state) => {
      state.gameData = {
        ...initialState.gameData,
        intents: []
      };
      state.isDetailedTasksLoading = false;
      // 🔥 Итерация 7: сброс инвентаря
      state.inventory = { ...EMPTY_INVENTORY };
      state.inventoryLoading = true;
      state.inventoryError = null;
    },

    // ============================================================================
    // 💬 ЧАТ
    // ============================================================================

    addChatMessage: (state, action) => {
      state.chatMessages.push(action.payload);
      if (state.chatMessages.length > 100) {
        state.chatMessages = state.chatMessages.slice(-100);
      }
    },

    clearChatMessages: (state) => {
      state.chatMessages = [];
    },

    resetGame: () => initialState,

    // 🔥 ============================================================================
    // 🔥 Итерация 7: ИНВЕНТАРЬ
    // 🔥 ============================================================================

    updateInventory: (state, action) => {
      state.inventory = normalizeInventoryPayload(action.payload);
      state.inventoryLoading = false;
      state.inventoryError = null;
    },

    setInventoryLoading: (state, action) => {
      state.inventoryLoading = Boolean(action.payload);
    },

    setInventoryError: (state, action) => {
      state.inventoryError = action.payload || null;
      state.inventoryLoading = false;
    },

    resetInventory: (state) => {
      state.inventory = { ...EMPTY_INVENTORY };
      state.inventoryLoading = true;
      state.inventoryError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadCharacters.pending, (state) => {
        state.loadingCharacters = true;
        state.error = null;
      })
      .addCase(loadCharacters.fulfilled, (state, action) => {
        state.loadingCharacters = false;
        
        state.characters = action.payload.characters.map(char => ({
          ...char,
          id: extractId(char),
          is_active: char.status === 'active',
          spawn_object_id: char.spawn_object_id || char.current_spawn_object_id || null,
          current_world_id: char.current_world_id || char.world_id || null,
          in_world: char.in_world === true || char.placed_in_world === true || false
        }));
        
        state.filteredCharacters = state.characters;
        state.mode = action.payload.mode || state.mode;
        
        state.stats = {
          total: state.characters.length,
          player: state.characters.filter(c => !c.is_npc).length,
          npc: state.characters.filter(c => c.is_npc).length,
          active: state.characters.filter(c => c.is_active).length,
          placed: state.characters.filter(c => c.in_world).length
        };
        
        const savedCharacterId = localStorage.getItem('selected_character_id');
        if (savedCharacterId) {
          const characterId = savedCharacterId;
          if (state.characters.some(c => c.id === characterId)) {
            state.selectedCharacter = characterId;
            const character = state.characters.find(c => c.id === characterId);
            state.selectedCharacterData = character || null;
          }
        }
      })
      .addCase(loadCharacters.rejected, (state, action) => {
        state.loadingCharacters = false;
        state.error = action.payload || 'Ошибка загрузки персонажей';
      })
      
      .addCase(selectCharacter.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(selectCharacter.fulfilled, (state, action) => {
        state.loading = false;
        
        const characterId = extractId(action.payload);
        
        if (validateId(characterId)) {
          const character = {
            ...action.payload,
            spawn_object_id: action.payload.spawn_object_id || action.payload.current_spawn_object_id || null,
            current_world_id: action.payload.current_world_id || action.payload.world_id || null,
            in_world: action.payload.in_world === true || action.payload.placed_in_world === true || false
          };
          
          state.selectedCharacter = characterId;
          state.selectedCharacterData = character;
          state.success = `Персонаж "${action.payload.name}" выбран`;
          
          const index = state.characters.findIndex(c => c.id === characterId);
          if (index !== -1) {
            state.characters[index] = character;
          }
          
          const filteredIndex = state.filteredCharacters.findIndex(c => c.id === characterId);
          if (filteredIndex !== -1) {
            state.filteredCharacters[filteredIndex] = character;
          }
        } else {
          state.error = 'Некорректные данные персонажа';
        }
      })
      .addCase(selectCharacter.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Ошибка выбора персонажа';
      })
      
      .addCase(createCharacter.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCharacter.fulfilled, (state, action) => {
        state.loading = false;
        
        const character = action.payload;
        const characterId = character.id;
        
        console.log('🎉 createCharacter.fulfilled:', character);
        
        if (!validateId(characterId)) {
          console.error('❌ Некорректный ID персонажа:', characterId);
          state.error = 'Сервер вернул некорректный ID персонажа';
          return;
        }
        
        const existingIndex = state.characters.findIndex(c => c.id === characterId);
        
        if (existingIndex === -1) {
          state.characters = [character, ...state.characters];
          state.filteredCharacters = [character, ...state.filteredCharacters];
          console.log('✅ Персонаж добавлен в список');
        } else {
          state.characters[existingIndex] = character;
          state.filteredCharacters[existingIndex] = character;
          console.log('✅ Персонаж обновлен в списке');
        }
        
        state.success = `Персонаж "${character.name}" успешно создан!`;
        console.log('✅ Состояние обновлено, персонаж готов');
      })
      .addCase(createCharacter.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Ошибка создания персонажа';
      })
      
      .addCase(updateCharacter.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCharacter.fulfilled, (state, action) => {
        state.loading = false;
        
        const characterId = extractId(action.payload);
        
        if (validateId(characterId)) {
          const character = {
            ...action.payload,
            id: characterId,
            spawn_object_id: action.payload.spawn_object_id || action.payload.current_spawn_object_id || null,
            current_world_id: action.payload.current_world_id || action.payload.world_id || null,
            in_world: action.payload.in_world === true || action.payload.placed_in_world === true || false
          };
          
          const index = state.characters.findIndex(c => c.id === characterId);
          if (index !== -1) {
            state.characters[index] = character;
          }
          
          const filteredIndex = state.filteredCharacters.findIndex(c => c.id === characterId);
          if (filteredIndex !== -1) {
            state.filteredCharacters[filteredIndex] = character;
          }
          
          if (state.selectedCharacter === characterId) {
            state.selectedCharacterData = character;
          }
          
          state.success = 'Изменения сохранены';
        } else {
          state.error = 'Некорректные данные обновленного персонажа';
        }
      })
      .addCase(updateCharacter.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Ошибка обновления персонажа';
      })
      
      .addCase(deleteCharacter.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteCharacter.fulfilled, (state, action) => {
        state.loading = false;
        
        const deletedId = action.payload;
        
        state.characters = state.characters.filter(c => c.id !== deletedId);
        state.filteredCharacters = state.filteredCharacters.filter(c => c.id !== deletedId);
        
        if (state.selectedCharacter === deletedId) {
          state.selectedCharacter = null;
          state.selectedCharacterData = null;
        }
        
        state.success = 'Персонаж удален';
      })
      .addCase(deleteCharacter.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Ошибка удаления персонажа';
      })
      
      .addCase(activateCharacter.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(activateCharacter.fulfilled, (state, action) => {
        state.loading = false;
        
        const character = {
          ...action.payload,
          spawn_object_id: action.payload.spawn_object_id || action.payload.current_spawn_object_id || null,
          current_world_id: action.payload.current_world_id || action.payload.world_id || null,
          in_world: action.payload.in_world === true || action.payload.placed_in_world === true || false
        };
        
        const characterId = character.id;
        
        if (validateId(characterId)) {
          const index = state.characters.findIndex(c => c.id === characterId);
          if (index !== -1) {
            state.characters[index] = character;
          }
          
          const filteredIndex = state.filteredCharacters.findIndex(c => c.id === characterId);
          if (filteredIndex !== -1) {
            state.filteredCharacters[filteredIndex] = character;
          }
          
          if (state.selectedCharacter === characterId) {
            state.selectedCharacterData = character;
          }
          
          state.success = `Персонаж "${character.name}" активирован`;
        } else {
          state.error = 'Некорректные данные активированного персонажа';
        }
      })
      .addCase(activateCharacter.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Ошибка активации персонажа';
      })
      
      .addCase(deactivateCharacter.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deactivateCharacter.fulfilled, (state, action) => {
        state.loading = false;
        
        const character = {
          ...action.payload,
          spawn_object_id: action.payload.spawn_object_id || action.payload.current_spawn_object_id || null,
          current_world_id: action.payload.current_world_id || action.payload.world_id || null,
          in_world: action.payload.in_world === true || action.payload.placed_in_world === true || false
        };
        
        const characterId = character.id;
        
        console.log('⏸️ deactivateCharacter.fulfilled:', character);
        
        if (validateId(characterId)) {
          const index = state.characters.findIndex(c => c.id === characterId);
          if (index !== -1) {
            state.characters[index] = character;
          }
          
          const filteredIndex = state.filteredCharacters.findIndex(c => c.id === characterId);
          if (filteredIndex !== -1) {
            state.filteredCharacters[filteredIndex] = character;
          }
          
          if (state.selectedCharacter === characterId) {
            state.selectedCharacter = null;
            state.selectedCharacterData = null;
          }
          
          state.success = `Персонаж "${character.name}" деактивирован`;
          console.log('✅ Состояние обновлено после деактивации');
        } else {
          state.error = 'Некорректные данные деактивированного персонажа';
        }
      })
      .addCase(deactivateCharacter.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Ошибка деактивации персонажа';
      });
  }
});

// ============================================================================
// ЭКСПОРТ
// ============================================================================

export const {
  setMode,
  setFilters,
  clearFilters,
  setSelectedCharacter,
  clearSelection,
  clearError,
  clearSuccess,
  setError,
  setSuccess,
  updateCharacterState,
  updateGameData,
  updateEmotions,
  updateGoals,
  updateTasks,
  updateIntents,
  updateLocationInfo,
  updateEnvironment,
  updateAvailableActions,
  updateConsciousness,
  updateConsciousnessDetails,
  updateDetailedTasks,
  setDetailedTasksLoading,
  resetGameData,
  addChatMessage,
  clearChatMessages,
  resetGame,
  // 🔥 Итерация 7: инвентарь
  updateInventory,
  setInventoryLoading,
  setInventoryError,
  resetInventory,
} = gameSlice.actions;

export default gameSlice.reducer;
